import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { UnitInput } from '../../components/ui/unit-input';
import { MoneyInput } from '../../components/ui/money-input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import { CharCounter, AtLimit } from '../../components/ui/char-counter';
import DateRangePicker from '../../components/ui/DateRangePicker';
import { WorkStatus, WorkStatusLabels, ProjectFormData, WorkCategoryWithPercentage, SavedBuildingTypeData } from '../../types';
import { CURRENCIES, DEFAULT_CURRENCY, Currency } from '../../types/currency';
import { ArrowLeft, Save, Building2, MapPin, Calendar, DollarSign, Users } from 'lucide-react';
import projectService from '../../services/projectService';
import workCategoryService from '../../services/workCategoryService';
import WorkCategorySelector, { WorkCategorySelectorData } from '../../components/Projects/WorkCategorySelector';
import MapLocationSelector from '../../components/Projects/MapLocationSelector';
import ImageUpload from '../../components/ui/ImageUpload';
import LoadingScreen from '../../components/ui/LoadingScreen';
import { useNavigate, useParams } from 'react-router-dom';
import { showSuccess, showError, showWarning } from '../../components/Alerts';

const ProjectEdit: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [selectedWorkCategories, setSelectedWorkCategories] = useState<WorkCategoryWithPercentage[]>([]);
  const [image1, setImage1] = useState<File | null>(null);
  const [image2, setImage2] = useState<File | null>(null);
  const [image3, setImage3] = useState<File | null>(null);

  // State for project data and saved building data
  const [project, setProject] = useState<ProjectFormData | null>(null);
  const [savedBuildingData, setSavedBuildingData] = useState<SavedBuildingTypeData | null>(null);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [nameFieldInvalid, setNameFieldInvalid] = useState(false);
  const [dateFieldInvalid, setDateFieldInvalid] = useState(false);

  const navigate = useNavigate();

  const [formData, setFormData] = useState<ProjectFormData>({
    id: 0,
    name: '',
    description: '',
    startDate: '',
    endDate: '',
    budget: 0,
    workCode: '',
    street: '',
    streetNumber: '',
    city: '',
    postalCode: '',
    country: 'Argentina',
    totalM2: 0,
    costPerM2: 0,
    totalEstimatedCost: 0,
    actualCost: 0,
    buildingType: '',
    projectStatus: WorkStatus.EN_CONSTRUCCION,
    client: '',
    latitude: -34.6037, // Buenos Aires por defecto
    longitude: -58.3816, // Buenos Aires por defecto
    image: null,
    currency: DEFAULT_CURRENCY
  });

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const projectId = parseInt(id!);

      const projectData = await projectService.getById(projectId);
      setProject(projectData);

      // Populate form data
      setFormData({
        id: projectData.id,
        name: projectData.name || '',
        description: projectData.description || '',
        startDate: projectData.startDate ? projectData.startDate.split('T')[0] : '',
        endDate: projectData.endDate ? projectData.endDate.split('T')[0] : '',
        budget: projectData.budget || 0,
        workCode: projectData.workCode || '',
        street: projectData.street || '',
        streetNumber: projectData.streetNumber || '',
        city: projectData.city || '',
        postalCode: projectData.postalCode || '',
        country: projectData.country || 'Argentina',
        totalM2: projectData.totalM2 || 0,
        costPerM2: projectData.costPerM2 || 0,
        totalEstimatedCost: projectData.totalEstimatedCost || 0,
        actualCost: projectData.actualCost || 0,
        buildingType: projectData.buildingType || '',
        projectStatus: projectData.projectStatus || WorkStatus.EN_CONSTRUCCION,
        client: projectData.client || '',
        latitude: projectData.latitude || -34.6037,
        longitude: projectData.longitude || -58.3816,
        image: null,
        currency: projectData.currency || DEFAULT_CURRENCY
      });

      // Preparar datos guardados del building type y work categories si existen
      if (projectData.buildingType && projectData.workCategories && projectData.workCategories.length > 0) {
        // Cargar todas las categorías para poder mapear la información completa
        const allWorkCategories = await workCategoryService.getAllWorkCategories();

        // Mapear los datos guardados con la información completa
        const fullWorkCategories: WorkCategoryWithPercentage[] = projectData.workCategories.map(saved => {
          const fullCategory = allWorkCategories.find(cat => cat.id === saved.id);
          return {
            id: saved.id,
            value: fullCategory?.value || '',
            description: fullCategory?.description || '',
            percentage: saved.percentage
          };
        });

        // Actualizar el estado local de categorías seleccionadas
        setSelectedWorkCategories(fullWorkCategories);

        setSavedBuildingData({
          buildingName: projectData.buildingType,
          workCategories: fullWorkCategories // Pasar fullWorkCategories aquí para que tenga todos los datos
        });
      }
    } catch (error) {
      console.error(error);
      showError('Error al cargar los datos del proyecto.');
    } finally {
      setLoading(false);
    }
  };

  // --- Form base ---

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    handleSave();
  };

  const handleSave = async () => {
    // Validar campo obligatorio: nombre del proyecto
    if (!formData.name || formData.name.trim() === '') {
      setNameFieldInvalid(true);
      showWarning('Por favor, completa el campo obligatorio', 'Nombre del Proyecto');
      document.getElementById('name')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      document.getElementById('name')?.focus();
      return;
    }

    // Validar campos obligatorios: fechas de inicio y fin
    if (!formData.startDate || !formData.endDate) {
      setDateFieldInvalid(true);
      showWarning('Por favor, completa el campo obligatorio', 'Selecciona las fechas de inicio y fin del proyecto');
      document.getElementById('projectDates')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    if (new Date(formData.endDate) <= new Date(formData.startDate)) {
      setDateFieldInvalid(true);
      showWarning('Fechas inválidas', 'La fecha de fin debe ser posterior a la fecha de inicio');
      document.getElementById('projectDates')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    // Validar que los rubros sumen 100% si hay un tipo de obra seleccionado
    const totalPercentage = selectedWorkCategories.reduce((sum, category) => sum + (category.percentage || 0), 0);
    if (!formData.buildingType) {
      showWarning('Falta seleccionar Tipo de Obra', 'Por favor seleccione y configure un tipo de obra.');
      return;
    }
    if (Math.abs(totalPercentage - 100) > 0.01) {
      showWarning('Revise los porcentajes de rubros', `Deben sumar exactamente 100%. Actualmente suman ${totalPercentage}%.`);
      return;
    }

    try {
      setSaving(true);

      // Incluir las categorías de trabajo seleccionadas en los datos del formulario
      const projectData = {
        ...formData,
        workCategories: selectedWorkCategories,
        // Asegurar que latitude y longitude sean números válidos o undefined
        latitude: typeof formData.latitude === 'number' ? formData.latitude : undefined,
        longitude: typeof formData.longitude === 'number' ? formData.longitude : undefined
      };

      await projectService.update(projectData);
      showSuccess('Proyecto actualizado exitosamente');

      // Solo navegar si todo fue exitoso
      navigate('/projects');
    } catch (error) {
      showError('Error al actualizar el proyecto. Por favor, intenta nuevamente.');
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (field: keyof ProjectFormData, value: any) => {
    // Si el campo es 'name' y tiene valor, quitar el estado de inválido
    if (field === 'name' && value && value.trim() !== '') {
      setNameFieldInvalid(false);
    }

    // Si los campos son fechas y tienen valor, quitar el estado de inválido
    if ((field === 'startDate' || field === 'endDate') && value) {
      setDateFieldInvalid(false);
    }

    setFormData(prev => {
      const newFormData = { ...prev, [field]: value };

      // Recalcular totalEstimatedCost si se cambió totalM2 o costPerM2
      if (field === 'totalM2' || field === 'costPerM2') {
        newFormData.totalEstimatedCost = (newFormData.totalM2 || 0) * (newFormData.costPerM2 || 0);
      }

      // Si se cambia el tipo de obra, limpiar las categorías de trabajo seleccionadas
      if (field === 'buildingType') {
        setSelectedWorkCategories([]);
      }

      return newFormData;
    });
  };

  const handleWorkCategoryChange = useCallback((data: WorkCategorySelectorData) => {
    setFormData(prev => ({ ...prev, buildingType: data.buildingTypeName }));
    setSelectedWorkCategories(data.workCategories);
  }, []);

  // Helper para mostrar valores numéricos (vacío si es 0)
  const getNumericDisplayValue = (value: number | undefined) => {
    return (value === 0 || value === undefined) ? '' : value.toString();
  };

  if (loading) {
    return <LoadingScreen message="Cargando datos del proyecto..." />;
  }

  if (saving) {
    return <LoadingScreen message="Actualizando proyecto..." />;
  }

  if (!project) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-foreground">Proyecto no encontrado</h2>
          <Button onClick={() => navigate('/projects')} className="mt-4">
            Volver a Proyectos
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-full p-4 space-y-6 sm:p-6">
      {/* Card Container */}
      <div className="overflow-hidden border rounded-lg shadow-sm bg-card border-border">
        {/* Header */}
        <div className="px-4 py-5 border-b sm:px-6 border-border">
          <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
            <div className="flex items-center gap-4">
              <Button variant="outline" onClick={() => navigate('/projects')} className="flex items-center gap-2 rounded-lg">
                <ArrowLeft className="w-4 h-4" />
                Volver
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-foreground">Editar Proyecto</h1>
                <p className="mt-1 text-sm text-muted-foreground">Modificar información del proyecto de construcción</p>
              </div>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="px-4 py-6 space-y-8 sm:px-6">
          {/* Información Básica */}
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
            <div className="lg:col-span-1">
              <h3 className="flex items-center gap-2 text-lg font-semibold text-foreground">
                <Building2 className="w-5 h-5 text-primary" />
                Información Básica
              </h3>
              <p className="mt-2 text-sm text-muted-foreground">
                Datos principales del proyecto de construcción.
              </p>
            </div>
            <div className="space-y-4 lg:col-span-2">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-xs font-medium text-foreground sm:text-sm">
                    Nombre del Proyecto <span className="text-xs text-red-500">(Campo obligatorio)</span>
                  </Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    maxLength={255}
                    className={`rounded-lg bg-background border-border focus-visible:ring-primary ${nameFieldInvalid ? 'border-red-500 border-2 focus-visible:ring-red-500' : ''
                      }`}
                    placeholder="Ingresa el nombre del proyecto"
                  />
                  <AtLimit value={formData.name} max={255} />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="workCode" className="text-xs font-medium text-foreground sm:text-sm">Código de Obra</Label>
                  <Input
                    id="workCode"
                    value={formData.workCode}
                    onChange={(e) => handleInputChange('workCode', e.target.value)}
                    maxLength={255}
                    className="rounded-lg bg-background border-border focus-visible:ring-primary"
                  />
                  <AtLimit value={formData.workCode} max={255} />
                </div>
              </div>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">

                <div className="space-y-2">
                  <Label htmlFor="workStatus" className="text-xs font-medium text-foreground sm:text-sm">Estado</Label>
                  <Select value={formData.projectStatus} onValueChange={(value) => handleInputChange('projectStatus', value)}>
                    <SelectTrigger className="rounded-lg bg-background border-border">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent sortAlphabetically>
                      {Object.entries(WorkStatusLabels).map(([key, label]) => (
                        <SelectItem key={key} value={key}>
                          {label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="description" className="text-xs font-medium text-foreground sm:text-sm">Descripción</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  maxLength={1000}
                  size="md"
                  placeholder="Describe los detalles del proyecto"
                />
                <CharCounter value={formData.description} max={1000} />
              </div>
            </div>
          </div>

          <div className="border-t border-border"></div>

          {/* Imagen del proyecto */}
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
            <div className="lg:col-span-1">
              <h3 className="flex items-center gap-2 text-lg font-semibold text-foreground">
                <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                Imágenes del Proyecto
              </h3>
              <p className="mt-2 text-sm text-muted-foreground">
                Sube hasta 3 imágenes representativas del proyecto.
              </p>
            </div>
            <div className="lg:col-span-2">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                <div className="w-full">
                  <div className="relative w-full" style={{ paddingBottom: '100%' }}>
                    <div className="absolute inset-0">
                      <ImageUpload
                        onImageChange={(file) => {
                          setImage1(file);
                          handleInputChange('image', file);
                        }}
                        currentImage={image1}
                        maxSizeMB={15}
                        acceptedFormats={['PNG', 'JPG', 'GIF']}
                      />
                    </div>
                  </div>
                </div>
                <div className="w-full">
                  <div className="relative w-full" style={{ paddingBottom: '100%' }}>
                    <div className="absolute inset-0">
                      <ImageUpload
                        onImageChange={(file) => {
                          setImage2(file);
                          handleInputChange('image2', file);
                        }}
                        currentImage={image2}
                        maxSizeMB={15}
                        acceptedFormats={['PNG', 'JPG', 'GIF']}
                      />
                    </div>
                  </div>
                </div>
                <div className="w-full">
                  <div className="relative w-full" style={{ paddingBottom: '100%' }}>
                    <div className="absolute inset-0">
                      <ImageUpload
                        onImageChange={(file) => {
                          setImage3(file);
                          handleInputChange('image3', file);
                        }}
                        currentImage={image3}
                        maxSizeMB={15}
                        acceptedFormats={['PNG', 'JPG', 'GIF']}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="border-t border-border"></div>

          {/* Financial Information */}
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
            <div className="lg:col-span-1">
              <h3 className="flex items-center gap-2 text-lg font-semibold text-foreground">
                <DollarSign className="w-5 h-5 text-primary" />
                Información Financiera
              </h3>
              <p className="mt-2 text-sm text-muted-foreground">
                Presupuesto, costos y valores del proyecto.
              </p>
            </div>
            <div className="space-y-4 lg:col-span-2">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                <div className="space-y-2">
                  <Label htmlFor="currency" className="text-xs font-medium text-foreground sm:text-sm">Moneda</Label>
                  <Select value={formData.currency} onValueChange={(value) => handleInputChange('currency', value)}>
                    <SelectTrigger className="rounded-lg bg-background border-border">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CURRENCIES.map((currency) => (
                        <SelectItem key={currency.value} value={currency.value}>
                          {currency.label} ({currency.symbol})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="budget" className="text-xs font-medium text-foreground sm:text-sm">Presupuesto</Label>
                  <MoneyInput
                    id="budget"
                    value={getNumericDisplayValue(formData.budget)}
                    onValueChange={(value) => handleInputChange('budget', value || 0)}
                    placeholder="0.00"
                    currency={formData.currency as Currency || DEFAULT_CURRENCY}
                    className="rounded-lg bg-background border-border focus-visible:ring-primary"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="actualCost" className="text-xs font-medium text-foreground sm:text-sm">Costo Actual</Label>
                  <MoneyInput
                    id="actualCost"
                    value={getNumericDisplayValue(formData.actualCost)}
                    onValueChange={(value) => handleInputChange('actualCost', value || 0)}
                    placeholder="0.00"
                    currency={formData.currency as Currency || DEFAULT_CURRENCY}
                    className="rounded-lg bg-background border-border focus-visible:ring-primary"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                <div className="space-y-2">
                  <Label htmlFor="totalM2" className="text-xs font-medium text-foreground sm:text-sm">M² Totales</Label>
                  <UnitInput
                    id="totalM2"
                    unit="m²"
                    value={getNumericDisplayValue(formData.totalM2)}
                    onValueChange={(value) => handleInputChange('totalM2', value || 0)}
                    placeholder="0.00"
                    className="rounded-lg bg-background border-border focus-visible:ring-primary"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="costPerM2" className="text-xs font-medium text-foreground sm:text-sm">Costo por M²</Label>
                  <MoneyInput
                    id="costPerM2"
                    value={getNumericDisplayValue(formData.costPerM2)}
                    onValueChange={(value) => handleInputChange('costPerM2', value || 0)}
                    placeholder="0.00"
                    currency={formData.currency as Currency || DEFAULT_CURRENCY}
                    className="rounded-lg bg-background border-border focus-visible:ring-primary"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="totalEstimatedCost" className="text-xs font-medium text-foreground sm:text-sm">Costo Total Estimado</Label>
                  <MoneyInput
                    id="totalEstimatedCost"
                    disabled
                    value={getNumericDisplayValue(formData.totalEstimatedCost)}
                    onValueChange={(value) => handleInputChange('totalEstimatedCost', value || 0)}
                    placeholder="0.00"
                    currency={formData.currency as Currency || DEFAULT_CURRENCY}
                    className="rounded-lg bg-background border-border"
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="border-t border-border"></div>


          {/* Rubros asociados al tipo de obra */}
          <WorkCategorySelector
            initialBuildingTypeName={formData.buildingType}
            initialWorkCategories={selectedWorkCategories}
            totalEstimatedCost={formData.totalEstimatedCost || 0}
            onChange={handleWorkCategoryChange}
            savedProjectBuildingType={savedBuildingData ? {
              name: savedBuildingData.buildingName,
              workCategories: savedBuildingData.workCategories
            } : null}
          />

          <div className="border-t border-border"></div>

          {/* Client Information */}
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
            <div className="lg:col-span-1">
              <h3 className="flex items-center gap-2 text-lg font-semibold text-foreground">
                <Users className="w-5 h-5 text-primary" />
                Cliente
              </h3>
              <p className="mt-2 text-sm text-muted-foreground">
                Información del cliente asociado al proyecto.
              </p>
            </div>
            <div className="lg:col-span-2">
              <div className="space-y-2">
                <Label htmlFor="client" className="text-xs font-medium text-foreground sm:text-sm">Ingrese nombre de cliente</Label>
                <Input
                  id="client"
                  type="text"
                  value={formData.client}
                  onChange={(e) => handleInputChange('client', e.target.value)}
                  maxLength={255}
                  className="rounded-lg bg-background border-border focus-visible:ring-primary"
                />
                <AtLimit value={formData.client} max={255} />
              </div>
            </div>
          </div>

          <div className="border-t border-border"></div>

          {/* Address Information */}
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
            <div className="lg:col-span-1">
              <h3 className="flex items-center gap-2 text-lg font-semibold text-foreground">
                <MapPin className="w-5 h-5 text-primary" />
                Dirección
              </h3>
              <p className="mt-2 text-sm text-muted-foreground">
                Ubicación física del proyecto de construcción.
              </p>
            </div>
            <div className="space-y-4 lg:col-span-2">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                <div className="space-y-2">
                  <Label htmlFor="street" className="text-xs font-medium text-foreground sm:text-sm">Calle</Label>
                  <Input
                    id="street"
                    value={formData.street}
                    onChange={(e) => handleInputChange('street', e.target.value)}
                    maxLength={255}
                    className="rounded-lg bg-background border-border focus-visible:ring-primary"
                  />
                  <AtLimit value={formData.street} max={255} />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="streetNumber" className="text-xs font-medium text-foreground sm:text-sm">Número</Label>
                  <Input
                    id="streetNumber"
                    value={formData.streetNumber}
                    onChange={(e) => handleInputChange('streetNumber', e.target.value)}
                    maxLength={255}
                    className="rounded-lg bg-background border-border focus-visible:ring-primary"
                  />
                  <AtLimit value={formData.streetNumber} max={255} />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="city" className="text-xs font-medium text-foreground sm:text-sm">Ciudad</Label>
                  <Input
                    id="city"
                    value={formData.city}
                    onChange={(e) => handleInputChange('city', e.target.value)}
                    maxLength={255}
                    className="rounded-lg bg-background border-border focus-visible:ring-primary"
                  />
                  <AtLimit value={formData.city} max={255} />
                </div>
              </div>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="postalCode" className="text-xs font-medium text-foreground sm:text-sm">Código Postal</Label>
                  <Input
                    id="postalCode"
                    value={formData.postalCode}
                    onChange={(e) => handleInputChange('postalCode', e.target.value)}
                    maxLength={255}
                    className="rounded-lg bg-background border-border focus-visible:ring-primary"
                  />
                  <AtLimit value={formData.postalCode} max={255} />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="country" className="text-xs font-medium text-foreground sm:text-sm">País</Label>
                  <Select value={formData.country} onValueChange={(value) => handleInputChange('country', value)}>
                    <SelectTrigger className="rounded-lg bg-background border-border">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent sortAlphabetically>
                      <SelectItem value="Argentina">Argentina</SelectItem>
                      <SelectItem value="Chile">Chile</SelectItem>
                      <SelectItem value="Uruguay">Uruguay</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <MapLocationSelector
                onLocationChange={(lat, lng) => {
                  setFormData(prev => ({ ...prev, latitude: lat, longitude: lng }));
                }}
                initialLat={formData.latitude}
                initialLng={formData.longitude}
                street={formData.street}
                streetNumber={formData.streetNumber}
                city={formData.city}
                postalCode={formData.postalCode}
                country={formData.country}
              />
            </div>
          </div>

          <div className="border-t border-border"></div>

          {/* Fechas del Proyecto */}
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
            <div className="lg:col-span-1">
              <h3 className="flex items-center gap-2 text-lg font-semibold text-foreground">
                <Calendar className="w-5 h-5 text-primary" />
                Fechas del Proyecto
              </h3>
              <p className="mt-2 text-sm text-muted-foreground">
                Período de ejecución del proyecto.
              </p>
            </div>
            <div className="lg:col-span-2">
              <div className="space-y-2">
                <Label htmlFor="projectDates" className="text-xs font-medium text-foreground sm:text-sm">
                  Seleccionar rango de fechas (Inicio - Fin) <span className="text-xs text-red-500">(Campo obligatorio)</span>
                </Label>
                <div className={dateFieldInvalid ? 'ring-2 ring-red-500 rounded-lg' : ''}>
                  <DateRangePicker
                    id="projectDates"
                    startDate={formData.startDate}
                    endDate={formData.endDate}
                    onRangeChange={(start, end) => {
                      handleInputChange('startDate', start);
                      handleInputChange('endDate', end);
                    }}
                    placeholder="Seleccionar fechas de inicio y fin del proyecto"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex flex-col-reverse gap-3 pt-4 border-t sm:flex-row sm:justify-end border-border">
            <Button type="button" variant="outline" className="w-full rounded-lg sm:w-auto" onClick={() => navigate('/projects')}>
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={saving}
              className="w-full rounded-lg bg-primary text-primary-foreground hover:opacity-90 sm:w-auto"
            >
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Actualizando...' : 'Actualizar Proyecto'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProjectEdit;
